import React from 'react';
import { useMutation } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import { StaffingRequestTemplateForm } from './_staffing-request-template-form';
import {
  CREATE_STAFFING_REQUEST_TEMPLATE_MUTATION,
  UPDATE_STAFFING_REQUEST_TEMPLATE_MUTATION,
} from 'works/legacy/graphql/staffing-request-template';
import { Drawer } from '@common/components';
import {
  CertificationType,
  SpecialtyType,
  FormValuesType,
} from './_staffing-request-template-form-types';

export const ACTIONS = { create: 'create', edit: 'edit', clone: 'clone' };

export function StaffingRequestTemplateDrawer({
  open,
  onClose,
  templateId,
  action = ACTIONS.create as 'create',
}: {
  open: boolean;
  onClose: () => void;
  templateId?: string;
  action?: 'create' | 'edit' | 'clone';
}) {
  const [createTemplate] = useMutation(
    CREATE_STAFFING_REQUEST_TEMPLATE_MUTATION,
    {
      onCompleted: () => {
        createMessage('success', 'Staffing Request Template has been created!');
        onClose();
      },
      onError: () => {
        createMessage(
          'error',
          'Failed to create Staffing Request Template. Please try again or contact your hiring partner.'
        );
      },
      refetchQueries: ['PaginatedStaffingRequestTemplatesList'],
    }
  );

  const [updateTemplate] = useMutation(
    UPDATE_STAFFING_REQUEST_TEMPLATE_MUTATION,
    {
      onCompleted: () => {
        createMessage('success', 'Staffing Request Template has been updated!');
        onClose();
      },
      onError: () => {
        createMessage(
          'error',
          'Failed to update Staffing Request Template. Please try again or contact your hiring partner.'
        );
      },
      refetchQueries: ['PaginatedStaffingRequestTemplatesList'],
    }
  );

  const handleSubmit = (
    values: FormValuesType,
    selectedSpecialties: SpecialtyType[],
    selectedCertifications: CertificationType[]
  ) => {
    const { costCenter, specialtyId, ...filteredValues } = values;

    const input = {
      ...filteredValues,
      specialties: selectedSpecialties.map((specialty) => ({
        clinicalSpecialtyId: specialty.id,
        required: specialty.required,
      })),
      certifications: selectedCertifications.map((cert) => ({
        certificationId: cert.id,
        required: cert.required,
      })),
    };

    if ([ACTIONS.create, ACTIONS.clone].includes(action)) {
      createTemplate({
        variables: {
          input,
        },
      });

      return;
    }

    if (action === ACTIONS.edit) {
      updateTemplate({
        variables: {
          staffingRequestTemplateId: templateId,
          input: {
            description: input.description,
            worksListTemplateId: input.worksListTemplateId,
            worksBillingConfigId: input.worksBillingConfigId,
            billingRegularRate: input.billingRegularRate,
            yearsOfExperience: input.yearsOfExperience,
            specialties: input.specialties,
            certifications: input.certifications,
            technologyIds: input.technologyIds,
            externalId: input.externalId,
          },
        },
      });

      return;
    }

    console.error(
      'StaffingRequestTemplateDrawer: Unknown action selected',
      action
    );
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={buildTitle(action, templateId)}
      className="StaffingRequestTemplateDrawer"
    >
      <StaffingRequestTemplateForm
        open={open}
        handleSubmit={handleSubmit}
        staffingRequestTemplateId={templateId}
        action={action}
        onClose={onClose}
      />
    </Drawer>
  );
}

function handleTitlePrefix(action: string, templateId?: string) {
  if (action === ACTIONS.create && templateId) {
    return 'Clone';
  }

  if (action === ACTIONS.edit) {
    return 'Edit';
  }

  return 'Create';
}
function buildTitle(action: string, templateId?: string) {
  return `${handleTitlePrefix(action, templateId)} Staffing Request Template`;
}
